<script setup lang="ts">
import { api } from '@/api'
import type Chatter from '@/api/resources/chatters/model'
import { getStatus } from '@/utils/status'
import { ElMessage, type UploadFile } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useChattersStore } from '@/stores/chatters'
import { ChatterStatus } from '@/api/resources/chatters/model'
import { useWebinarsStore } from '@/stores/webinars'
import { formatToHumanReadable } from '@/utils'
import Shifts from '@/components/Shifts.vue'
import ChatView from './ChatView.vue'
import { useAuthStore } from '@/stores/auth'
import { availableShifts } from '@/data/shifts'
import Nudges from '@/components/Nudges.vue'

const store = useChattersStore()
const webinarsStore = useWebinarsStore()
const authStore = useAuthStore()

const previousStatus = ref<ChatterStatus>(ChatterStatus.INIT)
const currentRoute = useRoute()
const id = computed(() => currentRoute.query.chatter as string)
const chatter = ref<Chatter>()
const selectedWebinarId = ref<number | null>(null)
const activeCollapsed = ref<string[]>([])

const init = async () => {
  if (!id.value) return
  chatter.value = await api.chatters.get(id.value)
  selectedWebinarId.value = chatter.value!.webinar?.id
  previousStatus.value = chatter.value!.status
  store.fetch()
  if (authStore.isCurator) {
    activeCollapsed.value = ['shifts']
    return
  }
  webinarsStore.fetch()
}

// The two onboarding routes end in different statuses: the test course in
// TEST_PASSED, and the conditions flow Russian speakers take in
// CONDITIONS_CONFIRMED. Both are ready for a curator to verify.
const couldBeVerified = computed(() => {
  return (
    previousStatus.value === ChatterStatus.TEST_PASSED ||
    previousStatus.value === ChatterStatus.CONDITIONS_CONFIRMED
  )
})

const verify = async () => {
  await api.verifyChatter({ urlParams: { id: Number(id.value) } })
  init()
  ElMessage.success('Verified')
}

const save = async () => {
  await api.chatters.update(id.value, {
    age: Number(chatter.value!.age),
    city: chatter.value!.city,
    country: chatter.value!.country,
    shifts: chatter.value!.shifts,
    name: chatter.value!.name,
    webinar_id: selectedWebinarId.value,
    invited_by_id: chatter.value!.invited_by_id,
    status: chatter.value!.status,
    comment: chatter.value!.comment,
    curator_id: chatter.value?.curator_id,
  })
  ElMessage.success('Saved')
  init()
}

watch(
  () => currentRoute.query.chatter,
  () => {
    if (currentRoute.query.chatter) {
      init()
    } else {
      chatter.value = undefined
    }
  },
  { immediate: true },
)
const photos = ref<UploadFile[]>([])
const uploadPhotos = async () => {
  if (photos.value.length > 0) {
    const formData = new FormData()
    photos.value.forEach((photo) => {
      if (photo.raw) {
        formData.append('photos', photo.raw)
      }
    })
    await api.uploadPhotos({
      payload: formData,
      urlParams: {
        id: id.value,
      },
    })
    photos.value = []
    init()
  }
}
const carouselSrcList = computed(() => {
  return chatter.value?.profile_photos.map((photo) => photoUrl(photo)) || []
})

const photoUrl = (photo: string) => {
  return `${import.meta.env.VITE_API_BASE_URL}/chatters/${id.value}/${photo}`
}

const referralLink = computed(() => {
  if (!chatter.value) {
    return ''
  }
  return `https://t.me/KDM_agency_bot?start=invitedBy=${chatter.value.tg_peers[0].username}`
})

const copyReferralLink = async () => {
  try {
    await navigator.clipboard.writeText(referralLink.value)
    ElMessage.success('Referral link copied to clipboard')
  } catch (error) {
    ElMessage.error('Failed to copy referral link')
  }
}

const completedAssessments = computed(() => {
  if (!chatter.value) return []
  return chatter.value.assessments.filter((a) => a.is_sent)
})

const getAssessmentStatus = (a: (typeof completedAssessments.value)[number]) => {
  if (a.has_passed === true) {
    const label = a.excellent_performance ? 'Passed ⭐' : 'Passed'
    return { label, type: 'success' as const }
  }
  if (a.has_passed === false) return { label: 'Failed', type: 'danger' as const }
  return { label: 'In progress', type: 'info' as const }
}

const getAttemptCount = (testId: number) => {
  if (!chatter.value) return 0
  return chatter.value.assessments.filter((a) => a.test_id === testId && a.is_sent).length
}
</script>

<template>
  <main v-if="chatter">
    <el-collapse v-model="activeCollapsed">
      <el-collapse-item title="Профиль" name="profile">
        <el-card shadow="never">
          <el-form :model="chatter" :disabled="authStore.isCurator">
            <el-form-item label="Telegram">
              <el-link :href="`https://t.me/${chatter.tg_peers[0].username}`" @click.stop>
                @{{ chatter.tg_peers[0].username }}</el-link
              >
            </el-form-item>
            <el-form-item label="Имя">
              <el-input v-model="chatter.name" />
            </el-form-item>
            <el-form-item label="Возраст">
              <el-input type="number" v-model="chatter.age" />
            </el-form-item>
            <el-form-item label="Город">
              <el-input v-model="chatter.city" />
            </el-form-item>
            <el-form-item label="Страна">
              <el-input v-model="chatter.country" />
            </el-form-item>
            <el-form-item label="Смены">
              <el-select v-model="chatter.shifts" multiple>
                <el-option
                  v-for="shift in availableShifts"
                  :key="shift"
                  :label="shift"
                  :value="shift"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Вебинар" v-if="!authStore.isCurator">
              <el-space>
                <el-select v-model="selectedWebinarId" filterable>
                  <el-option
                    v-for="webinar in webinarsStore.webinars"
                    :key="webinar.id"
                    :label="formatToHumanReadable(webinar.date)"
                    :value="webinar.id"
                  />
                </el-select>
                <RouterLink
                  v-if="chatter.webinar"
                  :to="{ query: { ...$route.query, webinar: chatter.webinar.id } }"
                >
                  Ссылка
                </RouterLink>
              </el-space>
            </el-form-item>
            <el-form-item label="Реферал" v-if="!authStore.isCurator">
              <el-space>
                <el-select v-model="chatter.invited_by_id" filterable>
                  <el-option
                    v-for="item in store.chatters"
                    :key="item.id"
                    :label="item.tg_peers[0].username"
                    :value="item.id"
                  />
                </el-select>

                <RouterLink
                  v-if="chatter.invited_by_id"
                  :to="{ query: { ...$route.query, chatter: chatter.invited_by_id } }"
                >
                  Профиль
                </RouterLink>
              </el-space>
            </el-form-item>
            <el-form-item label="Status">
              <el-select v-model="chatter.status">
                <el-option
                  v-for="status in Object.values(ChatterStatus)"
                  :key="status"
                  :label="getStatus(status).label"
                  :value="status"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Куратор" v-if="!authStore.isCurator">
              <el-select v-model="chatter.curator_id" filterable>
                <el-option
                  v-for="curator in store.curators"
                  :key="curator.id"
                  :label="curator.username"
                  :value="curator.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Комментарий">
              <el-input v-model="chatter.comment" type="textarea" />
            </el-form-item>
            <el-form-item>
              <el-button v-if="couldBeVerified" type="info" @click="verify">Verify</el-button>
              <el-button type="primary" @click="save">Save</el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <el-card v-if="!authStore.isCurator" shadow="never" style="margin-top: 20px">
          <el-space direction="vertical">
            <el-image
              v-if="carouselSrcList.length > 0"
              :src="carouselSrcList.at(-1)"
              :preview-src-list="carouselSrcList"
              class="photo"
            />
            <el-upload multiple :auto-upload="false" accept="image/*" v-model:file-list="photos">
              <el-button>
                <el-space
                  >Выбрать фото <el-icon><upload-filled /></el-icon> </el-space
              ></el-button>
              <el-button type="primary" @click.stop="uploadPhotos">Загрузить</el-button>
            </el-upload>
          </el-space>
        </el-card>
      </el-collapse-item>
      <el-collapse-item
        title="Тесты"
        name="assessments"
        v-if="completedAssessments.length > 0 && !authStore.isCurator"
      >
        <el-table :data="completedAssessments" row-key="id">
          <el-table-column type="expand">
            <template #default="{ row }">
              <div style="padding: 0 20px">
                <el-table :data="row.questions" size="small">
                  <el-table-column label="#" width="50" prop="index">
                    <template #default="{ row: q }">{{ q.index + 1 }}</template>
                  </el-table-column>
                  <el-table-column label="Question" prop="question" min-width="200" />
                  <el-table-column label="Answer" prop="user_answer" min-width="150">
                    <template #default="{ row: q }">
                      {{ q.user_answer ?? '—' }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Correct" prop="correct_answer" min-width="150" />
                  <el-table-column label="Result" width="100">
                    <template #default="{ row: q }">
                      <el-tag v-if="q.is_correct === true" type="success" size="small">OK</el-tag>
                      <el-tag v-else-if="q.is_correct === false" type="danger" size="small"
                        >Wrong</el-tag
                      >
                      <span v-else>—</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Explanation" prop="explanation" min-width="200">
                    <template #default="{ row: q }">
                      {{ q.explanation ?? '—' }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Test" width="80">
            <template #default="{ row }"> #{{ row.test_id }} </template>
          </el-table-column>
          <el-table-column label="Status" width="120">
            <template #default="{ row }">
              <el-tag :type="getAssessmentStatus(row).type" size="small">
                {{ getAssessmentStatus(row).label }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Score" width="80">
            <template #default="{ row }">
              <span v-if="row.score != null">{{ row.score }}/{{ row.questions.length }}</span>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="Attempt" width="80">
            <template #default="{ row }">
              {{ getAttemptCount(row.test_id) }}
            </template>
          </el-table-column>
          <el-table-column label="Completed" min-width="150">
            <template #default="{ row }">
              {{ row.completed_at ? formatToHumanReadable(row.completed_at) : '—' }}
            </template>
          </el-table-column>
          <el-table-column label="Reason" min-width="200">
            <template #default="{ row }">
              {{ row.reason ?? '—' }}
            </template>
          </el-table-column>
        </el-table>
      </el-collapse-item>
      <el-collapse-item title="Смены" name="shifts">
        <Shifts :chatter="chatter" @change="init" />
      </el-collapse-item>

      <el-collapse-item title="Рефералы" name="referals" v-if="!authStore.isCurator">
        <el-form-item label="Ссылка">
          <el-space direction="vertical" alignment="start">
            <el-input :model-value="referralLink" readonly style="width: 400px" />
            <el-button type="primary" @click="copyReferralLink">Copy</el-button>
          </el-space>
        </el-form-item>
        <el-table :data="chatter.invited_chatters">
          <el-table-column label="Name">
            <template #default="{ row }">
              <RouterLink :to="{ query: { ...$route.query, chatter: row.id } }">
                {{ row.name }}
              </RouterLink>
            </template>
          </el-table-column>
          <el-table-column label="Status">
            <template #default="{ row }: { row: Chatter }">
              <el-tag :type="getStatus(row.status).type">
                {{ getStatus(row.status).label }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-collapse-item>
      <el-collapse-item title="Подталкивания" name="nudges" v-if="!authStore.isCurator">
        <Nudges v-if="activeCollapsed.includes('nudges')" :chatter="chatter" @change="init" />
      </el-collapse-item>
      <el-collapse-item title="Переписка" name="chat" v-if="!authStore.isCurator">
        <ChatView v-if="activeCollapsed.includes('chat')" :chatter="chatter" />
      </el-collapse-item>
    </el-collapse>
    <el-backtop :right="50" :bottom="60" target=".el-overlay-dialog" />
  </main>
</template>

<style scoped>
.centered-space {
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}
.centered-space > * {
  min-width: 350px;
  flex-grow: 1;
}

.photo {
  margin: 1em auto;
  width: 300px;
}
</style>
